// src/modules/manualJournal/pages/ManualJournalPage.jsx

import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Row, Col, DatePicker, Button, Space, Spin, Alert, Tabs, message, Statistic,
  Empty, Typography, Select, Table, Divider, Tag,
} from 'antd';
import {
  ReloadOutlined, LockOutlined, CheckCircleOutlined, DownloadOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { getDayEntries, getDailySummary, finalizeDayEntries, getAccountLedger } from '../api';
import AddTransactionModal from '../components/AddTransactionModal';
import DayBalanceCard from '../components/DayBalanceCard';
import DailyJournalView from '../components/DailyJournalView';
import { ACCOUNT_CATEGORIES } from '../constants';

const { Title, Text } = Typography;

export default function ManualJournalPage() {
  const [journalDate, setJournalDate] = useState(dayjs());
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [summary, setSummary] = useState(null);
  
  // Ledger state
  const [ledgerCategory, setLedgerCategory] = useState('CASH_HAND');
  const [ledgerStartDate, setLedgerStartDate] = useState(dayjs().subtract(90, 'days'));
  const [ledgerEndDate, setLedgerEndDate] = useState(dayjs());
  const [ledgerData, setLedgerData] = useState(null);
  const [ledgerLoading, setLedgerLoading] = useState(false);

  const loadDayData = useCallback(async () => {
    setLoading(true);
    try {
      const dateStr = journalDate.toISOString();
      const [entriesRes, summaryRes] = await Promise.all([
        getDayEntries(dateStr),
        getDailySummary(dateStr),
      ]);

      setDayData(entriesRes.data);
      setSummary(summaryRes.data);
    } catch (err) {
      message.error('Failed to load journal data');
    } finally {
      setLoading(false);
    }
  }, [journalDate]);

  useEffect(() => {
    loadDayData();
  }, [journalDate, refreshTrigger]);

  const handleFinalize = async () => {
    try {
      const dateStr = journalDate.toISOString();
      await finalizeDayEntries(dateStr);
      message.success('Day entries finalized');
      setRefreshTrigger((v) => v + 1);
    } catch (err) {
      message.error(err.response?.data?.message || 'Failed to finalize');
    }
  };

  const loadLedger = async () => {
    setLedgerLoading(true);
    try {
      const res = await getAccountLedger(ledgerCategory, {
        startDate: ledgerStartDate.format('YYYY-MM-DD'),
        endDate: ledgerEndDate.format('YYYY-MM-DD'),
      });
      setLedgerData(res.data);
    } catch (err) {
      console.error('Failed to load ledger:', err);
      message.error('Failed to load ledger');
    } finally {
      setLedgerLoading(false);
    }
  };

  useEffect(() => {
    loadLedger();
  }, [ledgerCategory, ledgerStartDate, ledgerEndDate]);

  const totalDebit = summary?.summary ? Object.values(summary.summary).reduce((sum, cat) => sum + (cat.transactions || 0), 0) : 0;
  const totalCredit = dayData?.transactions?.length || 0;
  const isFinalized = dayData?.journalDay?.status === 'FINALIZED';

  // Ledger calculations
  const totalLedgerDebit = ledgerData?.ledgerEntries?.reduce((sum, entry) => {
    return sum + (entry.accountFrom === ledgerCategory ? entry.amount : 0);
  }, 0) || 0;

  const totalLedgerCredit = ledgerData?.ledgerEntries?.reduce((sum, entry) => {
    return sum + (entry.accountTo === ledgerCategory ? entry.amount : 0);
  }, 0) || 0;

  const ledgerOpeningBalance = ledgerData?.balances?.[0]?.openingBalance || 0;
  const ledgerClosingBalance = ledgerOpeningBalance + (totalLedgerCredit - totalLedgerDebit);

  const ledgerColumns = [
    {
      title: 'Date',
      key: 'date',
      width: 100,
      render: (_, record) => dayjs(record.transactionDate).format('DD-MM-YY'),
    },
    {
      title: 'Particulars',
      dataIndex: 'description',
      key: 'description',
      width: 250,
    },
    {
      title: 'Debit (₹)',
      key: 'debit',
      width: 120,
      align: 'right',
      render: (_, record) => {
        if (record.accountFrom === ledgerCategory) {
          return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{record.amount.toLocaleString('en-IN')}</span>;
        }
        return '';
      },
    },
    {
      title: 'Credit (₹)',
      key: 'credit',
      width: 120,
      align: 'right',
      render: (_, record) => {
        if (record.accountTo === ledgerCategory) {
          return <span style={{ color: '#52c41a', fontWeight: 'bold' }}>{record.amount.toLocaleString('en-IN')}</span>;
        }
        return '';
      },
    },
    {
      title: 'Balance (₹)',
      key: 'balance',
      width: 120,
      align: 'right',
      render: (_, record) => (
        <span style={{ 
          fontWeight: 'bold', 
          color: record.runningBalance < 0 ? '#ff4d4f' : '#1890ff' 
        }}>
          {record.runningBalance.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      title: 'Ref #',
      dataIndex: 'referenceNumber',
      key: 'reference',
      width: 100,
      render: (val) => val || '-',
    },
  ];

  const balanceSummaryColumns = [
    {
      title: 'Date',
      key: 'date',
      width: 120,
      render: (_, record) => dayjs(record.journalDay?.journalDate).format('DD-MM-YY'),
    },
    {
      title: 'Opening (₹)',
      dataIndex: 'openingBalance',
      key: 'opening',
      align: 'right',
      width: 140,
      render: (val) => val?.toLocaleString('en-IN'),
    },
    {
      title: 'Closing (₹)',
      dataIndex: 'closingBalance',
      key: 'closing',
      align: 'right',
      width: 140,
      render: (val) => val?.toLocaleString('en-IN'),
    },
    {
      title: 'Status',
      key: 'status',
      width: 130,
      render: (_, record) => (
        record.balanceOverridden ? 
          <Tag color="orange">Manual</Tag> : 
          <Tag color="blue">Auto</Tag>
      ),
    },
  ];

  return (
    <div style={{ padding: '20px' }}>
      <Title level={3} style={{ marginBottom: 24 }}>Manual Journaling Entry Book</Title>

      <Tabs
        items={[
          {
            key: 'daily-journal',
            label: 'Daily Journal',
            children: (
              <Spin spinning={loading}>
                <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                  <Col>Date Selection</Col>
                  <Col>
                    <Space wrap>
                      <DatePicker
                        value={journalDate}
                        onChange={setJournalDate}
                        format="DD/MM/YYYY"
                      />
                      <Button icon={<ReloadOutlined />} onClick={() => setRefreshTrigger((v) => v + 1)}>
                        Refresh
                      </Button>
                      {!isFinalized && (
                        <Button
                          type="primary"
                          danger
                          icon={<LockOutlined />}
                          onClick={() => {
                            if (window.confirm('Finalize this day? You won\'t be able to add/edit transactions.')) {
                              handleFinalize();
                            }
                          }}
                        >
                          Finalize Day
                        </Button>
                      )}
                      {isFinalized && (
                        <Button type="primary" disabled icon={<CheckCircleOutlined />}>
                          Finalized
                        </Button>
                      )}
                    </Space>
                  </Col>
                </Row>

                {isFinalized && (
                  <Alert
                    message="This day is finalized"
                    description="No changes can be made to this day's entries"
                    type="warning"
                    style={{ marginBottom: 16 }}
                    showIcon
                  />
                )}

                {/* Summary Stats */}
                <Card style={{ marginBottom: 16 }}>
                  <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={6}>
                      <Statistic
                        title="Total Transactions"
                        value={totalCredit}
                        precision={0}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Statistic
                        title="Day Status"
                        value={dayData?.journalDay?.status || 'NEW'}
                        valueStyle={{ color: isFinalized ? '#52c41a' : '#faad14' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Statistic
                        title="Created By"
                        value={dayData?.journalDay?.createdBy?.name || '—'}
                      />
                    </Col>
                    <Col xs={24} sm={12} lg={6}>
                      <Statistic
                        title="Date"
                        value={journalDate.format('DD/MM/YYYY')}
                      />
                    </Col>
                  </Row>
                </Card>

                {/* Account Balances */}
                <DayBalanceCard
                  journalDate={journalDate.toISOString()}
                  balances={dayData?.accountBalances || []}
                  onBalanceUpdated={() => setRefreshTrigger((v) => v + 1)}
                  loading={loading}
                />

                {/* Transactions */}
                <Card style={{ marginBottom: 16 }}>
                  <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
                    <Title level={5} style={{ margin: 0 }}>Daily Transactions</Title>
                    {!isFinalized && (
                      <AddTransactionModal
                        journalDate={journalDate.toISOString()}
                        onTransactionAdded={() => setRefreshTrigger((v) => v + 1)}
                        loading={loading}
                      />
                    )}
                  </Row>

                  <DailyJournalView
                    transactions={dayData?.transactions || []}
                    loading={loading}
                    journalDate={journalDate}
                    onTransactionDeleted={() => setRefreshTrigger((v) => v + 1)}
                    onTransactionUpdated={() => setRefreshTrigger((v) => v + 1)}
                  />
                </Card>

                {/* Summary Details */}
                {summary?.summary && (
                  <Card title="Summary Details">
                    <Row gutter={[16, 16]}>
                      {Object.entries(summary.summary).map(([category, data]) => {
                        const cat = ACCOUNT_CATEGORIES.find((c) => c.value === category);
                        return (
                          <Col key={category} xs={24} sm={12} lg={8}>
                            <Card size="small" style={{ textAlign: 'center' }}>
                              <div style={{ fontSize: 20, marginBottom: 8 }}>{cat?.icon}</div>
                              <Text strong>{cat?.label}</Text>
                              <div style={{ marginTop: 8, fontSize: 12, color: '#999' }}>
                                Opening: ₹{(data.opening || 0).toLocaleString('en-IN')}
                              </div>
                              <div style={{ fontSize: 12, color: '#999' }}>
                                Closing: ₹{(data.closing || 0).toLocaleString('en-IN')}
                              </div>
                              <div style={{ fontSize: 12, color: '#999' }}>
                                Transactions: {data.transactions || 0}
                              </div>
                            </Card>
                          </Col>
                        );
                      })}
                    </Row>
                  </Card>
                )}
              </Spin>
            ),
          },
          {
            key: 'account-ledger',
            label: 'Account Ledger',
            children: (
              <Spin spinning={ledgerLoading}>
                <Card style={{ marginBottom: 24 }}>
                  {/* Filters */}
                  <Row gutter={16} style={{ marginBottom: 24 }}>
                    <Col xs={24} sm={12} md={6}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>Account Category</label>
                      <Select
                        value={ledgerCategory}
                        onChange={setLedgerCategory}
                        style={{ width: '100%' }}
                        options={ACCOUNT_CATEGORIES.map(cat => ({
                          label: cat.label,
                          value: cat.value,
                        }))}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>From Date</label>
                      <DatePicker
                        value={ledgerStartDate}
                        onChange={setLedgerStartDate}
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <label style={{ display: 'block', marginBottom: 8, fontWeight: 500 }}>To Date</label>
                      <DatePicker
                        value={ledgerEndDate}
                        onChange={setLedgerEndDate}
                        style={{ width: '100%' }}
                        format="YYYY-MM-DD"
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
                      <Space>
                        <Button type="primary" onClick={loadLedger} loading={ledgerLoading}>
                          Load Ledger
                        </Button>
                        <Button icon={<DownloadOutlined />}>Export</Button>
                      </Space>
                    </Col>
                  </Row>

                  <Divider />

                  {/* Summary Statistics */}
                  <Row gutter={16} style={{ marginBottom: 32 }}>
                    <Col xs={24} sm={12} md={6}>
                      <Statistic
                        title="Opening Balance"
                        value={ledgerOpeningBalance}
                        precision={2}
                        prefix="₹"
                        valueStyle={{ color: '#1890ff' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Statistic
                        title="Total Debits"
                        value={totalLedgerDebit}
                        precision={2}
                        prefix="₹"
                        valueStyle={{ color: '#ff4d4f' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Statistic
                        title="Total Credits"
                        value={totalLedgerCredit}
                        precision={2}
                        prefix="₹"
                        valueStyle={{ color: '#52c41a' }}
                      />
                    </Col>
                    <Col xs={24} sm={12} md={6}>
                      <Statistic
                        title="Closing Balance"
                        value={ledgerClosingBalance}
                        precision={2}
                        prefix="₹"
                        valueStyle={{ color: ledgerClosingBalance < 0 ? '#ff4d4f' : '#1890ff', fontWeight: 'bold' }}
                      />
                    </Col>
                  </Row>

                  {/* Main Ledger Table */}
                  <div style={{ marginBottom: 32 }}>
                    <h3 style={{ marginBottom: 16 }}>Ledger Entries</h3>
                    <Table
                      columns={ledgerColumns}
                      dataSource={ledgerData?.ledgerEntries || []}
                      rowKey={(record, idx) => `${record._id}-${idx}`}
                      pagination={{
                        pageSize: 15,
                        showSizeChanger: true,
                        showTotal: (total) => `Total ${total} transactions`,
                      }}
                      scroll={{ x: 900 }}
                      size="middle"
                    />
                  </div>

                  {/* Balance Summary Table */}
                  {ledgerData?.balances && ledgerData.balances.length > 0 && (
                    <div>
                      <h3 style={{ marginBottom: 16 }}>Daily Balance Summary</h3>
                      <Table
                        columns={balanceSummaryColumns}
                        dataSource={ledgerData.balances}
                        rowKey={(record) => record._id}
                        pagination={false}
                        size="small"
                      />
                    </div>
                  )}
                </Card>
              </Spin>
            ),
          },
        ]}
      />
    </div>
  );
}
